import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Shield, Users, FileText, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { AUTH_BASE_URL } from '../config/api';

const PrivacySettings = () => {
  const { user } = useAuth();
  const { isSuperAdmin } = usePermissions();
  const [settings, setSettings] = useState({
    viewAllChats: false,
    viewAllFiles: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${AUTH_BASE_URL}/api/users/settings`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings({
          viewAllChats: data.viewAllChats || false,
          viewAllFiles: data.viewAllFiles || false,
        });
      }
    } catch (error) {
      console.error('Failed to load privacy settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value) => {
    try {
      setSaving(true);
      const response = await fetch(`${AUTH_BASE_URL}/api/users/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [key]: value }),
      });

      if (response.ok) {
        setSettings(prev => ({ ...prev, [key]: value }));
      } else {
        throw new Error('Failed to update setting');
      }
    } catch (error) {
      console.error('Failed to update privacy setting:', error);
      alert('Failed to update setting. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="p-6 text-center">
        <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Super Admin Only</h3>
        <p className="text-gray-600">
          Privacy & Security settings are only available to Super Administrators.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading privacy settings...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="border-b pb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Shield className="w-5 h-5 text-blue-600" />
          Privacy & Security
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Super Admin privacy controls for accessing user data
        </p>
      </div>

      {/* Warning Banner */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-yellow-800">Privacy Notice</h3>
            <p className="text-sm text-yellow-700 mt-1">
              These settings allow you to view all user data. Use responsibly and only when necessary for administrative purposes.
              All access is logged for security and compliance.
            </p>
          </div>
        </div>
      </div>

      {/* View All Chats Setting */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">View All Conversations</h3>
              <p className="text-sm text-gray-600 mt-1">
                See all user conversations in the sidebar with user indicators.
                This allows you to monitor and moderate chat content across all users.
              </p>
            </div>
          </div>
          <div className="ml-4">
            <button
              onClick={() => updateSetting('viewAllChats', !settings.viewAllChats)}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.viewAllChats ? 'bg-blue-600' : 'bg-gray-200'
              } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.viewAllChats ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
        {settings.viewAllChats && (
          <div className="mt-3 flex items-center gap-2 text-sm">
            <Eye className="w-4 h-4 text-green-600" />
            <span className="text-green-700 font-medium">Active</span>
            <span className="text-gray-600">- All user conversations are visible</span>
          </div>
        )}
      </div>

      {/* View All Files Setting */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <FileText className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">View All Files</h3>
              <p className="text-sm text-gray-600 mt-1">
                Access all uploaded files from all users in the Files Manager.
                This includes documents, images, and other files uploaded by any user.
              </p>
            </div>
          </div>
          <div className="ml-4">
            <button
              onClick={() => updateSetting('viewAllFiles', !settings.viewAllFiles)}
              disabled={saving}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                settings.viewAllFiles ? 'bg-purple-600' : 'bg-gray-200'
              } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.viewAllFiles ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
        {settings.viewAllFiles && (
          <div className="mt-3 flex items-center gap-2 text-sm">
            <Eye className="w-4 h-4 text-green-600" />
            <span className="text-green-700 font-medium">Active</span>
            <span className="text-gray-600">- All user files are accessible</span>
          </div>
        )}
      </div>

      {/* Status Summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-3">Current Privacy Status</h3>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Conversations Access:</span>
            <span className={`font-medium ${settings.viewAllChats ? 'text-orange-600' : 'text-green-600'}`}>
              {settings.viewAllChats ? 'All Users' : 'Personal Only'}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Files Access:</span>
            <span className={`font-medium ${settings.viewAllFiles ? 'text-orange-600' : 'text-green-600'}`}>
              {settings.viewAllFiles ? 'All Users' : 'Personal Only'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacySettings;